import React, { useState } from 'react';
import { makeStyles } from '../library';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Paper,
    IconButton,
    Tooltip,
    Typography,
    SimpleDraggable as Draggable,
    Box,
    Stepper,
    Step,
    StepLabel
} from '../library';

import { Button } from '../';
import * as icons from '../icons';

const useStyles = makeStyles(theme => ({
    wrapper: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        '& > *': {
            marginLeft: theme.spacing(1)
        }
    },
    modal: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    paper: {
        backgroundColor: theme.palette.background.paper,
        boxShadow: theme.shadows[20],
        padding: theme.spacing(2, 4, 3)
    },
    actions: {
        display: 'flex',
        justifyContent: 'space-between',
        padding: theme.spacing(2)
    },
    stepContent: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2),
        minHeight: '200px'
    }
}));

const PaperComponent = props => {
    return (
        <Draggable
            handle='#draggable-dialog-title'
            cancel={'[class*="MuiDialogContent-root"]'}
        >
            <Paper {...props} />
        </Draggable>
    );
};

/**
 * WizardModal component that displays a modal with step-by-step navigation
 *
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the modal is open
 * @param {Function} props.close - Function to close the modal
 * @param {string} props.title - Title of the modal
 * @param {Array} props.components - Array of components to display as steps
 * @param {Array} props.stepLabels - Array of labels for the steps
 * @param {Function} props.onFinish - Function to call when the wizard is finished
 * @param {Object} props.rest - Additional props to pass to the Dialog component
 */
const WizardModal = props => {
    const {
        open,
        close,
        title,
        components = [],
        stepLabels = [],
        onFinish,
        ...rest
    } = props;

    const classes = useStyles();
    const [activeStep, setActiveStep] = useState(0);
    const maxSteps = components.length;

    // If no components are provided, don't render anything
    if (!open || components.length === 0) return null;

    // Generate step labels if not provided
    const labels =
        stepLabels.length === components.length
            ? stepLabels
            : components.map((_, index) => `Paso ${index + 1}`);

    const handleNext = () => {
        setActiveStep(prevActiveStep => prevActiveStep + 1);
    };

    const handleBack = () => {
        setActiveStep(prevActiveStep => prevActiveStep - 1);
    };

    const handleFinish = () => {
        if (onFinish) {
            onFinish();
        }
        close();
    };

    const handleCancel = () => {
        setActiveStep(0);
        close();
    };

    return (
        <Dialog
            open={open}
            PaperComponent={PaperComponent}
            aria-labelledby='draggable-dialog-title'
            maxWidth='md'
            fullWidth
            {...rest}
        >
            <DialogTitle style={{ cursor: 'move' }} id='draggable-dialog-title'>
                <div className={classes.wrapper}>
                    <Tooltip title={'Cerrar'}>
                        <IconButton size='small' onClick={handleCancel}>
                            <icons.CloseIcon />
                        </IconButton>
                    </Tooltip>
                    <Typography variant='h6'>{title}</Typography>
                </div>
            </DialogTitle>
            <DialogContent className={classes.paper}>
                <Stepper activeStep={activeStep}>
                    {labels.map((label, index) => (
                        <Step key={index}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>
                <Box className={classes.stepContent}>
                    {components[activeStep]}
                </Box>
            </DialogContent>
            <DialogActions className={classes.actions}>
                <Box>
                    <Button
                        toolTipText='Cancelar'
                        icon={<icons.CancelIcon />}
                        onClick={handleCancel}
                    />
                </Box>
                <Box>
                    <Button
                        toolTipText='Anterior'
                        icon={<icons.PreviousIcon />}
                        onClick={handleBack}
                        disabled={activeStep === 0}
                    />
                    {activeStep === maxSteps - 1 ? (
                        <Button
                            toolTipText='Finalizar'
                            icon={<icons.OkIcon />}
                            onClick={handleFinish}
                        />
                    ) : (
                        <Button
                            toolTipText='Siguiente'
                            icon={<icons.NextIcon />}
                            onClick={handleNext}
                        />
                    )}
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default WizardModal;
